import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  setSearchQuery,
  setGroupingOption,
  setSortingOption,
  setYearRange,
  setMonths,
  setSelectedTags,
  togglePolygonSelection,
  toggleGroupSelection,
  selectAllPolygons,
  selectAllGroups,
  deselectAllPolygons,
  deselectAllGroups,
  fetchExplorerData,
  fetchAnalyticsData,
  fetchAvailableYears,
  clearSearchAndPolygonData,
  setHistoryFilters,
  setHistoryAttributeValues,
  fetchHistoryAttributeValues,
  setGroupComparisonGroups,
  clearGroupComparisonResult,
  fetchGroupComparisonData,
  resetAllFilters,
} from '../../store/dataExplorerSlice';
import SearchFilterControls from '../common/SearchFilterControls';
import { getAllTags, getAllCountries, getHistoryAttributeValues } from '../../services/apiClient';
import PolygonSelectionPanel from '../common/PolygonSelectionPanel';
import NEEChart from '../charts/NEEChart';
import { GroupingOption, ChartType, NEEDisplayMode } from '../../types/enums';
import type { DataExplorerState } from '../../store/dataExplorerSlice';
import { AnnualNEEData, MonthlyNEEData, ComparisonGroupDefinition } from '../../types/schema';
import { HistoricalBaselineTool } from './HistoricalBaselineTool';
import { useAccountContext } from '../../hooks/useAccountContext';
import { HistoryFilterPopup } from '../common/HistoryFilterPopup';
import GroupComparisonBuilder from '../analytics/GroupComparisonBuilder';
import GroupComparisonModal from '../analytics/GroupComparisonModal';
import DownloadDataModal from '../common/DownloadDataModal';
import VerticalAlignBottomOutlinedIcon from '@mui/icons-material/VerticalAlignBottomOutlined';
import { SummarySection } from '../common/SummarySection';
import SummaryCard from '../common/SummaryCard';
import { formatArea, formatNEE, formatIntensity } from '../../utils/formatters';

const emptyArray: (AnnualNEEData[] | MonthlyNEEData[]) = [];

const AnalyticsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  overflow: 'auto',
}));

const ContentGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '350px 1fr',
  gap: theme.spacing(3),
  height: 'calc(100vh - 250px)',
}));

const AnalyticsView: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [annualDisplayMode, setAnnualDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const [monthlyDisplayMode, setMonthlyDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [isHistoryFilterOpen, setHistoryFilterOpen] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  const {
    searchQuery,
    groupingOption,
    sortingOption,
    startYear,
    endYear,
    months,
    selectedTags,
    selectedPolygonIds,
    selectedGroupIds,
    polygons,
    polygonGroups,
    analytics,
    status,
    historyFilters,
    historyAttributeValues,
    groupComparisonGroups,
    groupComparisonResult,
    groupComparisonStatus,
    groupComparisonError,
    isYearRangeFinal,
    historyAttributeStatus,
  } = useSelector((state: RootState) => state.dataExplorer as DataExplorerState);
  const { selectedAccount, isAdmin } = useSelector((state: RootState) => state.app);
  const { accountId } = useAccountContext();

  const [isLoadingPolygons, setIsLoadingPolygons] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [isComparisonBuilderOpen, setComparisonBuilderOpen] = useState(false);
  const [isComparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [comparisonAnnualDisplayMode, setComparisonAnnualDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const [comparisonMonthlyDisplayMode, setComparisonMonthlyDisplayMode] = useState<NEEDisplayMode>(NEEDisplayMode.TOTAL);
  const isComparisonLoading = groupComparisonStatus === 'loading';

  // Removing this to retain polygon / group selection on toggling Analytics & Map views
  // useEffect(() => {
  //   // Return a cleanup function to run when the component unmounts
  //   return () => {
  //     dispatch(clearSearchAndPolygonData());
  //   };
  // }, [dispatch]);

  // Fetch available years on mount and when account changes
  useEffect(() => {
    dispatch(fetchAvailableYears()).then((result) => {
      if (fetchAvailableYears.fulfilled.match(result)) {
        const years = result.payload.years;
        if (years && years.length > 0) {
          setAvailableYears(years);
        }
      }
    });
  }, [dispatch, accountId]);

  const fetchTags = useCallback(async () => {
    try {
      const tags = await getAllTags(isAdmin ? selectedAccount?.accountId : undefined);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setAvailableTags([]);
    }
  }, [isAdmin, selectedAccount?.accountId]);

  const fetchCountries = useCallback(async () => {
    try {
      const countries = await getAllCountries(isAdmin ? selectedAccount?.accountId : undefined);
      setAvailableCountries(countries);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      setAvailableCountries([]);
    }
  }, [isAdmin, selectedAccount?.accountId]);

  useEffect(() => {
    if (availableTags.length === 0) {
      fetchTags();
    }
  }, [availableTags.length, fetchTags]);

  useEffect(() => {
    if (availableCountries.length === 0) {
      fetchCountries();
    }
  }, [availableCountries.length, fetchCountries]);

  useEffect(() => {
    if (!isYearRangeFinal) return;
    setIsLoadingPolygons(true);
    dispatch(fetchExplorerData()).finally(() => {
      setIsLoadingPolygons(false);
    });
  }, [dispatch, isYearRangeFinal, groupingOption, startYear, endYear, months, selectedAccount, searchQuery, selectedTags, historyFilters]);

  useEffect(() => {
    if (!isYearRangeFinal) return;
    dispatch(fetchAnalyticsData());
  }, [dispatch, isYearRangeFinal, selectedPolygonIds, selectedGroupIds, startYear, endYear, months, selectedAccount, selectedTags, historyFilters]);

  const handlePolygonToggle = (polygonId: number) => {
    dispatch(togglePolygonSelection(polygonId as number));
  };

  const handleGroupToggle = (groupId: string) => {
    dispatch(toggleGroupSelection(groupId));
  };

  const handleSelectAll = () => {
    if (groupingOption === GroupingOption.NONE) {
        const allPolygonIds = polygons.map(p => p.id);
        dispatch(selectAllPolygons(allPolygonIds as number[]));
    } else {
        const allGroupIds = polygonGroups.map(g => g.id);
        dispatch(selectAllGroups(allGroupIds));
    }
  };

  const handleDeselectAll = () => {
    if (groupingOption === GroupingOption.NONE) {
        dispatch(deselectAllPolygons());
    } else {
        dispatch(deselectAllGroups());
    }
  };

  const handleGroupingChange = useCallback((option: GroupingOption) => {
    dispatch(setGroupingOption(option));
  }, [dispatch]);

  const handleComparisonOpen = async () => {
    if (groupComparisonGroups.length < 2) {
      const defaults = [
        { label: 'Group 1', filters: {} },
        { label: 'Group 2', filters: {} },
      ];
      dispatch(setGroupComparisonGroups(defaults));
    }
    if (Object.keys(historyAttributeValues).length === 0) {
      dispatch(fetchHistoryAttributeValues());
    }
    // Fetch data_type values for group comparison
    if (!historyAttributeValues.data_type) {
      const accountId = isAdmin ? selectedAccount?.accountId : undefined;
      try {
        const dataTypeValues = await getHistoryAttributeValues(['data_type'], accountId);
        // Merge data_type values into historyAttributeValues
        const updatedValues = { ...historyAttributeValues, ...dataTypeValues };
        dispatch(setHistoryAttributeValues(updatedValues));
      } catch (error) {
        console.error('Failed to fetch data_type values:', error);
      }
    }
    setComparisonBuilderOpen(true);
  };

  const handleComparisonGroupsChange = (groups: ComparisonGroupDefinition[]) => {
    dispatch(setGroupComparisonGroups(groups));
  };

  const handleComparisonSubmit = () => {
    dispatch(fetchGroupComparisonData())
      .unwrap()
      .then(() => {
        setComparisonBuilderOpen(false);
      })
      .catch(() => {
        // Keep drawer open to display validation errors
      });
  };

  useEffect(() => {
    if (groupComparisonResult && groupComparisonStatus === 'succeeded' && !isComparisonBuilderOpen) {
      setComparisonModalOpen(true);
    }
  }, [groupComparisonResult, groupComparisonStatus, isComparisonBuilderOpen]);

  const handleComparisonClear = () => {
    dispatch(clearGroupComparisonResult());
  };

  const handleDownloadClick = () => {
    setDownloadModalOpen(true);
  };

  const annualChartTitle = useMemo(() => {
    return months && months.length > 0 ? "Aggregate NEE" : "Annual NEE";
  }, [months]);

  const hasFiltersApplied = useMemo(() => {
    const hasMonths = months && months.length > 0;
    const hasTags = selectedTags && selectedTags.length > 0;
    const hasPolygons = selectedPolygonIds && selectedPolygonIds.length > 0;
    const hasGroups = selectedGroupIds && selectedGroupIds.length > 0;
    const hasHistoryFilters = historyFilters && Object.keys(historyFilters).length > 0 &&
      Object.values(historyFilters).some(values => values && values.length > 0);
    const hasSearch = searchQuery && searchQuery.trim().length > 0;

    return hasMonths || hasTags || hasPolygons || hasGroups || hasHistoryFilters || hasSearch;
  }, [months, selectedTags, selectedPolygonIds, selectedGroupIds, historyFilters, searchQuery]);

  const handleResetAll = () => {
    dispatch(resetAllFilters());
  };

  return (
    <AnalyticsContainer>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Analytics
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<VerticalAlignBottomOutlinedIcon />}
              onClick={handleDownloadClick}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Data
            </Button>
            <Button variant="contained" onClick={handleComparisonOpen} sx={{ whiteSpace: 'nowrap' }}>
              Compare Groups
            </Button>
          </Stack>
        </Stack>

        <SearchFilterControls
          searchQuery={searchQuery}
          onSearchChange={(query) => dispatch(setSearchQuery(query))}
          startYear={startYear}
          onStartYearChange={(year) => dispatch(setYearRange({ startYear: year, endYear }))}
          endYear={endYear}
          onEndYearChange={(year) => dispatch(setYearRange({ startYear, endYear: year }))}
          months={months}
          onMonthsChange={(newMonths) => dispatch(setMonths(newMonths))}
          availableTags={availableTags}
          selectedTags={selectedTags}
          onTagsChange={(tags) => dispatch(setSelectedTags(tags))}
          availableYears={availableYears.length > 0 ? availableYears : undefined}
          onHistoryFilterClick={() => setHistoryFilterOpen(true)}
          historyFilters={historyFilters}
          onResetAll={handleResetAll}
        />

        <ContentGrid>
          <PolygonSelectionPanel
            groupingOption={groupingOption}
            onGroupingChange={handleGroupingChange}
            sortingOption={sortingOption}
            onSortingChange={(option) => dispatch(setSortingOption(option))}
            polygons={polygons}
            polygonGroups={polygonGroups}
            selectedPolygons={selectedPolygonIds as number[]}
            selectedGroups={selectedGroupIds}
            onPolygonToggle={handlePolygonToggle}
            onGroupToggle={handleGroupToggle}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            title={groupingOption === GroupingOption.NONE ? `Polygons (${polygons.length})` : `Groups (${polygonGroups.length})`}
            loading={isLoadingPolygons}
          />
          <Box>
            <SummarySection>
              <SummaryCard
                title="Total Number of Polygons"
                value={analytics ? analytics.kpiData.polygonCount.toLocaleString() : '--'}
                unit="polygons"
                loading={status === 'loading'}
                tooltip="A polygon is a single farm, field, or forest"
              />
              <SummaryCard
                title="Total Area"
                value={analytics ? formatArea(analytics.kpiData.totalArea, false) : '--'}
                unit="ha"
                loading={status === 'loading'}
              />
              <SummaryCard
                title="NEE Total"
                value={analytics ? formatNEE(analytics.kpiData.neePerYear, 'tCO2', 2, false) : '--'}
                unit="tCO2/yr"
                isNegative={true}
                loading={status === 'loading'}
                tooltip="Average annual NEE for all polygons. A negative value means net CO2 sequestration, a positive value net CO2 emission."
              />
              <SummaryCard
                title="Average Annual NEE Intensity"
                value={analytics ? formatIntensity(analytics.kpiData.neeIntensityPerYear, 2, false) : '--'}
                unit="tCO2/ha/yr"
                isNegative={true}
                loading={status === 'loading'}
                tooltip="Average annual NEE per hectare for all polygons. A negative value means net CO2 sequestration, a positive value net CO2 emission."
              />
            </SummarySection>
            <NEEChart
              title={annualChartTitle}
              chartType={ChartType.ANNUAL}
              data={analytics?.annualData || emptyArray}
              displayMode={annualDisplayMode}
              onDisplayModeChange={setAnnualDisplayMode}
              height={400}
              loading={status === 'loading'}
              selectedMonths={months}
              hasSelectedPolygons={selectedPolygonIds.length > 0 || selectedGroupIds.length > 0}
              hasFiltersApplied={hasFiltersApplied}
            />
            <Box sx={{ mt: 3 }}>
              <NEEChart
                title="Monthly NEE"
                chartType={ChartType.MONTHLY}
                data={analytics?.monthlyData || emptyArray}
                displayMode={monthlyDisplayMode}
                onDisplayModeChange={setMonthlyDisplayMode}
                height={400}
                loading={status === 'loading'}
                hasSelectedPolygons={selectedPolygonIds.length > 0 || selectedGroupIds.length > 0}
                hasFiltersApplied={hasFiltersApplied}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Paper elevation={1} sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Historical Baseline Tool
                </Typography>
                {groupingOption === GroupingOption.NONE && selectedPolygonIds.length > 0 ? (
                  <HistoricalBaselineTool />
                ) : (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Switch to no grouping and select at least one polygon to view this section.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </ContentGrid>
      </Stack>
      <HistoryFilterPopup
        open={isHistoryFilterOpen}
        onClose={() => setHistoryFilterOpen(false)}
        onApply={(filters) => dispatch(setHistoryFilters(filters))}
        initialFilters={historyFilters}
        attributeValues={historyAttributeValues}
        isLoading={historyAttributeStatus === 'loading'}
        onFetchValues={() => dispatch(fetchHistoryAttributeValues())}
      />
      <GroupComparisonBuilder
        open={isComparisonBuilderOpen}
        groups={groupComparisonGroups}
        availableTags={availableTags}
        availableCountries={availableCountries}
        historyAttributeValues={historyAttributeValues}
        startYear={startYear}
        endYear={endYear}
        availableYears={availableYears.length > 0 ? availableYears : undefined}
        onStartYearChange={(year) => dispatch(setYearRange({ startYear: year, endYear }))}
        onEndYearChange={(year) => dispatch(setYearRange({ startYear, endYear: year }))}
        isSubmitting={isComparisonLoading}
        error={groupComparisonStatus === 'failed' ? groupComparisonError : null}
        hasResult={!!groupComparisonResult}
        onGroupsChange={handleComparisonGroupsChange}
        onSubmit={handleComparisonSubmit}
        onClose={() => setComparisonBuilderOpen(false)}
        onClearResults={handleComparisonClear}
      />
      <GroupComparisonModal
        open={isComparisonModalOpen}
        onClose={() => setComparisonModalOpen(false)}
        result={groupComparisonResult}
        loading={isComparisonLoading}
        error={groupComparisonStatus === 'failed' ? groupComparisonError : null}
        annualDisplayMode={comparisonAnnualDisplayMode}
        monthlyDisplayMode={comparisonMonthlyDisplayMode}
        onAnnualDisplayModeChange={setComparisonAnnualDisplayMode}
        onMonthlyDisplayModeChange={setComparisonMonthlyDisplayMode}
      />
      <DownloadDataModal
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        availableYears={availableYears}
        is_geometry={false}
      />
    </AnalyticsContainer>
  );
};

export default AnalyticsView;
